import { useState } from "react";
import {
  BODY_PARTS, EQUIPMENT, equipmentOptions, foldText, MAX_REST_SEC, MEASURES, newExerciseDraft, saveExercise,
  type BodyPart, type Exercise, type ExerciseDraft, type ExerciseError,
} from "../core/exercises";
import type { Backend } from "../core/storage";
import { useBack } from "./back";
import { formatDuration, t } from "./texts";

const OWN = "__own";
const REST_STEP = 15;

/**
 * Create or edit a glossary entry. Kept self-contained (saves through `saveExercise`) so the template editor and
 * the running workout can open it too ("create a new one right there").
 */
export function ExerciseEditor(props: {
  db: Backend;
  exercises: Exercise[];
  initial?: Exercise;
  onSaved(e: Exercise): void;
  onCancel(): void;
}) {
  const [draft, setDraft] = useState<ExerciseDraft>(() => props.initial ?? newExerciseDraft());
  const options = equipmentOptions(props.exercises);
  const [ownEquipment, setOwnEquipment] = useState(!options.includes(draft.equipment));
  const [errors, setErrors] = useState<ExerciseError[]>([]);
  const [busy, setBusy] = useState(false);
  useBack(props.onCancel);
  const set = (patch: Partial<ExerciseDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setErrors([]);
  };

  const save = async () => {
    setBusy(true);
    // an own value typed like a default label ("Kurzhantel") is stored as that default's key
    const known = EQUIPMENT.find((k) => foldText(t.equipment(k)) === foldText(draft.equipment));
    const r = await saveExercise(props.db, { ...draft, equipment: known ?? draft.equipment });
    setBusy(false);
    if (r.ok) props.onSaved(r.exercise);
    else setErrors(r.errors);
  };
  const err = (k: ExerciseError) => errors.includes(k) && <p className="error">{t.exerciseError(k)}</p>;

  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={props.onCancel}>{t.cancel}</button>
        <button className="primary" disabled={busy} onClick={save}>{t.save}</button>
      </header>
      <h1>{props.initial ? t.editor.titleEdit : t.editor.titleNew}</h1>

      <label className="label">{t.editor.name}
        <input className="field" value={draft.name} placeholder={t.editor.namePlaceholder} autoFocus={!props.initial}
          onChange={(e) => set({ name: e.target.value })} />
      </label>
      {err("nameMissing")}{err("nameTaken")}

      <label className="label">{t.editor.equipment}
        <select className="field" value={ownEquipment ? OWN : draft.equipment}
          onChange={(e) => {
            const own = e.target.value === OWN;
            setOwnEquipment(own);
            set({ equipment: own ? "" : e.target.value });
          }}>
          {options.map((k) => <option key={k} value={k}>{t.equipment(k)}</option>)}
          <option value={OWN}>{t.editor.ownEquipment}</option>
        </select>
      </label>
      {ownEquipment && (
        <input className="field" value={draft.equipment} placeholder={t.editor.ownEquipmentPlaceholder} autoFocus
          onChange={(e) => set({ equipment: e.target.value })} />
      )}
      {err("equipmentMissing")}

      <label className="label">{t.editor.bodyPart}
        <select className="field" value={draft.bodyPart} onChange={(e) => set({ bodyPart: e.target.value as BodyPart })}>
          {BODY_PARTS.map((k) => <option key={k} value={k}>{t.bodyPart(k)}</option>)}
        </select>
      </label>

      <div className="label">{t.editor.measure}</div>
      <div className="choices">
        {MEASURES.map((m) => (
          <button key={m} className={"choice" + (draft.measure === m ? " active" : "")} onClick={() => set({ measure: m })}>
            <span className="name">{t.measure(m)}</span>
            <span className="sub">{t.measureHint(m)}</span>
          </button>
        ))}
      </div>

      <div className="label">{t.editor.rest}</div>
      <div className="stepper">
        <button className="secondary" disabled={draft.restSec <= 0}
          onClick={() => set({ restSec: Math.max(0, draft.restSec - REST_STEP) })}>−{REST_STEP}</button>
        <span className="value">{formatDuration(draft.restSec)}</span>
        <button className="secondary" disabled={draft.restSec >= MAX_REST_SEC}
          onClick={() => set({ restSec: Math.min(MAX_REST_SEC, draft.restSec + REST_STEP) })}>+{REST_STEP}</button>
      </div>
      {err("restInvalid")}

      <label className="label">{t.editor.notes}
        <textarea className="field" rows={3} value={draft.notes} placeholder={t.editor.notesPlaceholder}
          onChange={(e) => set({ notes: e.target.value })} />
      </label>

      <button className="primary wide" disabled={busy} onClick={save}>{t.save}</button>
    </>
  );
}
